import { Component, OnInit, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// Import the interfaces
import { Fase, Usuario } from '../../models/fase';

// Required services and models
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AppealsService } from '../../services/appeals.service';
import { DatosDenuncia } from '../../../shared/models/datosDenuncia';
import { DatosApelacion } from '../../../model/datosApelacion';
import { environment } from 'src/environments/environment.prod';

@Component({
  selector: 'app-accept-appeals',
  templateUrl: './accept-appeals.component.html',
  styleUrls: ['./accept-appeals.component.css']
})
export class AcceptAppealsComponent implements OnInit {
  assetsUrl: string = environment.baseAssetsUrl;

  @Input() denuncia: DatosDenuncia;
  @Input() apelacionActual: DatosApelacion;

  validatingForm: FormGroup;
  phases: Fase[] = []; // List of phases
  users: Usuario[] = []; // Users filtered by the selected phase
  show: boolean = false;
  state = true; // Controls the [disabled] state of the button
  selectedPhase: Fase | null = null;
  selectedUser: Usuario | null = null;

  constructor(
    private appealsService: AppealsService,
    private toastr: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private apiService: ApiService,
    private auth: AuthService
  ) { }

  ngOnInit(): void {
    this.validatingForm = new FormGroup({
      phase: new FormControl('', [Validators.required]),
      user: new FormControl({ value: '', disabled: true }, [Validators.required]),
    });


    const society = this.auth.getSociedad();
    const language = this.auth.getLanguage();

    this.appealsService.getPhasesWithUsers(society, language).subscribe({
      next: (listPhases: Fase[]) => {
        this.phases = listPhases;
      },
      error: () => {
        this.router.navigateByUrl('/RefreshComponent', { skipLocationChange: true }).then(() => {
          this.router.navigate(['dashboard/complaint-management/mis-denuncias']);
        });
      }
    });

    // Listen for changes in the phase selection to enable and filter users
    this.validatingForm.get('phase')?.valueChanges.subscribe(phaseId => {
      if (phaseId !== null && phaseId !== undefined) {
        this.selectedPhase =
          this.phases.find(f => f.id_fase === phaseId) || null;

        if (this.selectedPhase) {
          // Apply conflict filters (rejected receptor & compliance)
          this.filterConflictUsers(this.selectedPhase.usuarios);
        } else {
          this.users = [];
        }

        this.validatingForm.get('user')?.enable();
        this.validatingForm.get('user')?.reset();
        this.state = true;
      } else {
        this.selectedPhase = null;
        this.users = [];
        this.validatingForm.get('user')?.disable();
      }
    });

    // Listen for changes in the user selection to enable the button
    this.validatingForm.get('user')?.valueChanges.subscribe(userId => {
      if (userId) {
        this.selectedUser = this.users.find(u => u.id_user === userId) || null;
        this.state = false;
      } else {
        this.selectedUser = null;
        this.state = true;
      }
    });
  }

  // Accessors to facilitate access to the form controls
  get phase() {
    return this.validatingForm.get('phase');
  }

  get user() {
    return this.validatingForm.get('user');
  }

  /**
   * Filters users with conflicts:
   * - rejected receptor
   * - users rejected by compliance (CSV list)
   */
  private filterConflictUsers(list: Usuario[]): void {
    if (!list || list.length === 0) {
      this.users = [];
      return;
    }

    const rejectReceptor =
      this.denuncia?.id_receptor_rechazado
        ? String(this.denuncia.id_receptor_rechazado)
        : null;

    // Fetch compliance rejected users and filter
    this.apiService.searchUserComplianceByIdSocietyRole(
      this.auth.getSociedad(),
      this.denuncia.id_denuncia
    )
      .subscribe({
        next: (compResp: any) => {
          const compList = compResp || [];
          const rawCompReject =
            compList.length > 0
              ? compList[0].id_usuario_comp_rechazado || null
              : null;

          const compRejectIds: string[] = rawCompReject
            ? rawCompReject.split(',').map((s: string) => s.trim()).filter(Boolean)
            : [];

          this.users = list.filter((u: any) => {
            const uid = String(u.id_user ?? '');
            if (rejectReceptor && uid === rejectReceptor) return false;
            if (compRejectIds.includes(uid)) return false;
            return true;
          });
        },
        error: () => {
          this.users = rejectReceptor
            ? list.filter(u => String(u.id_user ?? '') !== rejectReceptor)
            : list;
        }
      });
  }

  // Handle click on user select to show error if no users available
  onUserSelectClick(): void {
    if (!this.users || this.users.length === 0) {
      this.toastr.error(this.translate.instant('texto_933'));
    }
  }



  // Method to confirm acceptance
  confirmAccept() {
    const idPhase = this.phase?.value;
    const idUser = this.user?.value;

    this.appealsService.acceptAppeal(
      this.denuncia.id_denuncia,
      this.apelacionActual.id_apelacion,
      idPhase,
      idUser
    ).subscribe({
      next: (resp) => {
        this.toastr.success(this.translate.instant('texto_847'), this.translate.instant('texto_846'));
        this.router.navigateByUrl('/RefreshComponent', { skipLocationChange: true }).then(() => {
          this.router.navigate(['dashboard/complaint-management/mis-denuncias']);
        });
      },
      error: (err) => {
        this.toastr.error(this.translate.instant('texto_847'), this.translate.instant('texto_848'));
      },
    });
  }

}
